import { useState, useEffect } from 'react';
import COUNTRIES from '../../utils/Countries';
import GENRES from '../../utils/Genres';
import izenuAPI from '../../services/izenuAPI';
import { AUDIO_ACCEPT, AUDIO_FORMATS_HINT, AUDIO_QUALITY_HINT, audioFileProblem, uploadTrackAudio } from '../../utils/audioUpload';
import { AiOutlinePlusCircle } from 'react-icons/ai';

const uuid = () => {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let str = '';
    for (let i = 0; i < 10; i++) {
        str += chars[Math.floor(Math.random() * chars.length)];
    }
    return str;
};

const inputStyle = { width: '100%', marginBottom: 16, padding: '8px 12px', borderRadius: 8, border: '1px solid #4b5563', background: '#374151', color: '#f9fafb', fontSize: 14, boxSizing: 'border-box' };
const labelStyle = { display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#9ca3af' };
const hintStyle = { margin: '0 0 16px', fontSize: 11, color: '#6b7280' };

export default function UploadArtistMusic() {
    const [image, setImage] = useState(null);
    const [song, setSong] = useState(null);
    const [title, setTitle] = useState('');
    const [tags, setTags] = useState('');
    const [genre, setGenre] = useState('Kuduro');
    const [country, setCountry] = useState('Angola');
    const [link, setLink] = useState('');
    const [isrc, setIsrc] = useState('');
    const [version, setVersion] = useState('original');
    const [explicit, setExplicit] = useState('0');
    const [duration, setDuration] = useState('');
    const [dataList, setDataList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadIndex, setUploadIndex] = useState(0);
    const [successMsg, setSuccessMsg] = useState('');

    // Release states
    const [releases, setReleases] = useState([]);
    const [selectedRelease, setSelectedRelease] = useState(null);
    const [loadingReleases, setLoadingReleases] = useState(false);
    const [showCreateRelease, setShowCreateRelease] = useState(false);
    const [newReleaseTitle, setNewReleaseTitle] = useState('');
    const [newReleaseType, setNewReleaseType] = useState('album');
    const [newReleaseUpc, setNewReleaseUpc] = useState('');
    const [newReleaseGenre, setNewReleaseGenre] = useState('');
    const [creatingRelease, setCreatingRelease] = useState(false);
    const [trackNumber, setTrackNumber] = useState('');

    const artistId = localStorage.getItem('id');

    // Fetch artist releases on mount
    useEffect(() => {
        if (artistId) fetchArtistReleases();
    }, [artistId]);

    const fetchArtistReleases = async () => {
        setLoadingReleases(true);
        try {
            const { data: r } = await izenuAPI.get('/release/fetchArtistReleases.php?artistId=' + artistId + '&limit=100&include_drafts=1');
            if (r.response && Array.isArray(r.data)) {
                setReleases(r.data);
            } else {
                setReleases([]);
            }
        } catch (err) {
            console.error('Error fetching releases', err);
            setReleases([]);
        }
        setLoadingReleases(false);
    };

    const createNewRelease = async () => {
        if (!newReleaseTitle.trim()) {
            alert('O título do release é obrigatório');
            return;
        }
        setCreatingRelease(true);
        try {
            const { data: r } = await izenuAPI.post('/release/create.php', {
                title: newReleaseTitle.trim(),
                type: newReleaseType,
                genre: newReleaseGenre || genre || null,
                upc: newReleaseUpc || null,
                status: 'draft'
            });
            if (r.response && r.release_id) {
                await fetchArtistReleases();
                const newRelease = {
                    id: r.release_id,
                    title: newReleaseTitle.trim(),
                    type: newReleaseType,
                    track_count: 0
                };
                setSelectedRelease(newRelease);
                setShowCreateRelease(false);
                setNewReleaseTitle('');
                setNewReleaseType('album');
                setNewReleaseUpc('');
                setNewReleaseGenre('');
            } else {
                alert('Erro ao criar release: ' + (r.message || 'Tenta mais tarde'));
            }
        } catch (err) {
            console.error('Error creating release', err);
            alert('Erro ao criar release');
        }
        setCreatingRelease(false);
    };

    const handleCoverChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        if (!['png', 'jpg', 'jpeg'].includes(ext)) {
            alert('Tipo de imagem inválido. Use PNG ou JPG.');
            e.target.value = '';
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setImage({ file, result: reader.result });
        reader.readAsDataURL(file);
    };

    const handleMusicChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const problem = audioFileProblem(file);
        if (problem) {
            alert(problem);
            e.target.value = '';
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        const audio = new Audio(objectUrl);
        audio.onloadedmetadata = () => {
            setDuration(Math.round(audio.duration * 1000));
            URL.revokeObjectURL(objectUrl);
        };
        setSong(file);
    };

    const uploadFilesToS3 = async (data) => {
        const id = uuid();
        const d = new Date();
        const date = { day: d.getUTCDate(), year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };

        const imgExt = data.image.file.type.split('/')[1];
        const cover_name = `izenu_cover_${date.year}_${date.month}_${date.day}_${id}.${imgExt}`;
        const userId = localStorage.getItem('id');

        // Upload cover
        const coverFd = new FormData();
        coverFd.append('files', data.image.file);
        coverFd.append('key', `users/${userId}/songs/artworks/${cover_name}`);
        await izenuAPI.post(`${process.env.REACT_APP_API_URL}/upload/uploadSongCover.php`, coverFd);

        // Upload song, straight to S3 (utils/audioUpload.js)
        const song_name = await uploadTrackAudio(data.song);

        // Insert into database
        const payload = {
            title: data.title,
            genre: data.genre,
            country: data.country,
            tags: data.tags || '',
            link: data.link || '',
            isrc: data.isrc || '',
            version: data.version || 'original',
            explicit: data.explicit || '0',
            cover: cover_name,
            music_file: song_name,
            duration: data.duration,
        };

        if (data.release_id) {
            payload.release_id = data.release_id;
        }
        if (data.track_number) {
            payload.track_number = data.track_number;
        }

        await izenuAPI.post('/artist/insertMusic.php', payload);
    };

    const resetForm = () => {
        const audioInput = document.querySelector('.file-audio');
        const imageInput = document.querySelector('.file-photo');
        if (audioInput) audioInput.value = '';
        if (imageInput) imageInput.value = '';
        setTitle('');
        setTags('');
        setLink('');
        setIsrc('');
        setVersion('original');
        setExplicit('0');
        setImage(null);
        setSong(null);
        setDuration('');
        setSelectedRelease(null);
        setTrackNumber('');
    };

    const addToList = () => {
        if (!title.trim()) { alert('Introduz o título da música'); return; }
        if (!image) { alert('Seleciona uma capa para a música'); return; }
        if (!song) { alert('Seleciona o ficheiro de música'); return; }

        setDataList(prev => [...prev, {
            title, tags, genre, country, link, isrc, version, explicit,
            image, song, duration,
            release_id: selectedRelease?.id || null,
            track_number: trackNumber || null,
            release_title: selectedRelease?.title || null,
        }]);
        resetForm();
    };

    const removeFromList = (index) => {
        setDataList(prev => prev.filter((_, i) => i !== index));
    };

    const uploadAll = async () => {
        if (dataList.length === 0) return;
        setUploading(true);
        setUploadIndex(0);

        for (let i = 0; i < dataList.length; i++) {
            setUploadIndex(i + 1);
            try {
                await uploadFilesToS3(dataList[i]);
            } catch (err) {
                console.log(err);
                alert(`Erro ao carregar "${dataList[i].title}". Tenta novamente.`);
                setUploading(false);
                return;
            }
        }

        setUploading(false);
        setDataList([]);
        setSuccessMsg(`${dataList.length} música(s) carregada(s) com sucesso! A música ficará pendente até ser aprovada pelo admin.`);
        setTimeout(() => setSuccessMsg(''), 8000);
    };

    return (
        <div className="min-h-screen bg-gray-900" style={{ paddingBottom: 80 }}>
            {/* Success banner */}
            {successMsg && (
                <div style={{ background: '#059669', color: '#fff', padding: '10px 20px', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>
                    {successMsg}
                </div>
            )}

            {/* Upload overlay */}
            {uploading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                    <svg aria-hidden="true" className="w-10 h-10 animate-spin text-gray-600 fill-amber-400" viewBox="0 0 100 101" fill="none">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                    <p style={{ color: '#fbbf24', fontSize: 20, fontWeight: 700 }}>A carregar música {uploadIndex} de {dataList.length}</p>
                    <p style={{ color: '#d1d5db', fontSize: 14 }}>Não feches nem actualizes a janela</p>
                </div>
            )}

            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #312e81, #581c87)', padding: '28px 40px' }}>
                <h1 style={{ margin: 0, color: '#f9fafb', fontSize: 24, fontWeight: 700 }}>Carregar música</h1>
                <p style={{ margin: '4px 0 0', color: '#c4b5fd', fontSize: 14 }}>Adiciona músicas à fila e faz upload de todas de uma vez</p>
            </div>

            {/* Form */}
            <form onSubmit={e => { e.preventDefault(); addToList(); }} style={{ padding: '24px 40px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    {/* Left column — files */}
                    <div style={{ background: '#1f2937', borderRadius: 12, padding: 24 }}>
                        <h3 style={{ margin: '0 0 20px', color: '#f9fafb', fontSize: 16, fontWeight: 600 }}>Ficheiros</h3>

                        <label style={labelStyle}>Música (MP3, WAV ou FLAC)</label>
                        <input type="file" accept={AUDIO_ACCEPT} onChange={handleMusicChange}
                            className="file-audio"
                            style={{ width: '100%', marginBottom: 6, fontSize: 13, color: '#d1d5db', background: '#374151', border: '1px solid #4b5563', borderRadius: 8, padding: '8px 12px', boxSizing: 'border-box' }} />
                        {song && <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6366f1' }}>{song.name}</p>}
                        {!song && <p style={hintStyle}>{AUDIO_FORMATS_HINT} {AUDIO_QUALITY_HINT}</p>}

                        <label style={labelStyle}>Capa (PNG ou JPG)</label>
                        <input type="file" accept="image/jpg,image/jpeg,image/png" onChange={handleCoverChange}
                            className="file-photo"
                            style={{ width: '100%', marginBottom: 6, fontSize: 13, color: '#d1d5db', background: '#374151', border: '1px solid #4b5563', borderRadius: 8, padding: '8px 12px', boxSizing: 'border-box' }} />
                        {image ? (
                            <div style={{ marginTop: 8 }}>
                                <img src={image.result} alt="Preview" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover' }} />
                            </div>
                        ) : (
                            <p style={{ margin: 0, fontSize: 11, color: '#6b7280' }}>PNG ou JPG, inferior a 2 MB</p>
                        )}
                    </div>

                    {/* Right column — metadata */}
                    <div style={{ background: '#1f2937', borderRadius: 12, padding: 24, maxHeight: 600, overflowY: 'auto' }}>
                        <h3 style={{ margin: '0 0 20px', color: '#f9fafb', fontSize: 16, fontWeight: 600 }}>Metadados</h3>

                        <label style={labelStyle}>Título da música</label>
                        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Título da música"
                            style={inputStyle} />

                        {/* Release selector */}
                        <label style={labelStyle}>Release (Opcional)</label>
                        <select
                            value={selectedRelease?.id || ''}
                            onChange={e => {
                                const rid = e.target.value;
                                if (!rid) {
                                    setSelectedRelease(null);
                                    setTrackNumber('');
                                } else {
                                    const r = releases.find(r => String(r.id) === rid);
                                    setSelectedRelease(r || null);
                                    setTrackNumber('');
                                }
                            }}
                            style={inputStyle}
                        >
                            <option value="">Nenhum (será criado um Single)</option>
                            {loadingReleases && <option disabled>A carregar...</option>}
                            {releases.map(r => (
                                <option key={r.id} value={r.id}>
                                    {r.title} ({r.type}{r.track_count > 0 ? ` · ${r.track_count} faixa${r.track_count !== 1 ? 's' : ''}` : ''})
                                </option>
                            ))}
                        </select>

                        {/* Create new release */}
                        {!showCreateRelease && (
                            <div style={{ marginTop: -8, marginBottom: 16 }}>
                                <button type="button" onClick={() => setShowCreateRelease(true)}
                                    style={{ background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}>
                                    <AiOutlinePlusCircle size={15} /> Criar novo release
                                </button>
                            </div>
                        )}

                        {showCreateRelease && (
                            <div style={{ marginBottom: 16, padding: 16, border: '1px solid #4338ca', borderRadius: 8, background: '#1e1b4b' }}>
                                <label style={labelStyle}>Título do novo release</label>
                                <input type="text" value={newReleaseTitle} onChange={e => setNewReleaseTitle(e.target.value)}
                                    placeholder="Nome do release" style={{ ...inputStyle, marginBottom: 12 }} />
                                <label style={labelStyle}>Tipo</label>
                                <select value={newReleaseType} onChange={e => setNewReleaseType(e.target.value)}
                                    style={{ ...inputStyle, marginBottom: 12 }}>
                                    <option value="single">Single</option>
                                    <option value="ep">EP</option>
                                    <option value="album">Álbum</option>
                                </select>
                                <label style={labelStyle}>UPC (Opcional)</label>
                                <input type="text" value={newReleaseUpc} onChange={e => setNewReleaseUpc(e.target.value)}
                                    placeholder="Código UPC/EAN" style={{ ...inputStyle, marginBottom: 12 }} />
                                <label style={labelStyle}>Género</label>
                                <select value={newReleaseGenre} onChange={e => setNewReleaseGenre(e.target.value)}
                                    style={{ ...inputStyle, marginBottom: 12 }}>
                                    <option value="">Selecionar género</option>
                                    {GENRES.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                                </select>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button type="button" disabled={creatingRelease} onClick={createNewRelease}
                                        style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#6366f1', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: creatingRelease ? 0.6 : 1 }}>
                                        {creatingRelease ? 'A criar...' : 'Criar Release'}
                                    </button>
                                    <button type="button" onClick={() => { setShowCreateRelease(false); setNewReleaseTitle(''); setNewReleaseType('album'); setNewReleaseUpc(''); setNewReleaseGenre(''); }}
                                        style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #4b5563', background: 'transparent', color: '#9ca3af', fontSize: 13, cursor: 'pointer' }}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Track number for multi-track releases */}
                        {selectedRelease && selectedRelease.type !== 'single' && (
                            <>
                                <label style={labelStyle}>Número da faixa</label>
                                <input type="number" min="1" value={trackNumber} onChange={e => setTrackNumber(e.target.value)}
                                    placeholder={`Faixa ${(selectedRelease.track_count || 0) + 1}`}
                                    style={inputStyle} />
                                <p style={{ ...hintStyle, marginTop: -10 }}>Este release já tem {selectedRelease.track_count || 0} faixa(s)</p>
                            </>
                        )}

                        <label style={labelStyle}>Tags</label>
                        <input type="text" value={tags} onChange={e => setTags(e.target.value)}
                            placeholder="Adicionar tags que descrevem a música"
                            style={inputStyle} />

                        <label style={labelStyle}>Género</label>
                        <select value={genre} onChange={e => setGenre(e.target.value)} style={inputStyle}>
                            {GENRES.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                        </select>

                        <label style={labelStyle}>País</label>
                        <select value={country} onChange={e => setCountry(e.target.value)} style={inputStyle}>
                            {COUNTRIES.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                        </select>

                        <label style={labelStyle}>Link (Opcional)</label>
                        <input type="text" value={link} onChange={e => setLink(e.target.value)} placeholder="Adicionar link"
                            style={inputStyle} />

                        <label style={labelStyle}>ISRC (Opcional)</label>
                        <p style={{ ...hintStyle, marginBottom: 8 }}>
                            Código Internacional de Gravação Sonora. Formato: CC-XXX-YY-NNNNN (ex: AO-Z03-24-00001).
                        </p>
                        <input type="text" value={isrc} onChange={e => setIsrc(e.target.value)} placeholder="Ex: AO-Z03-24-00001"
                            style={inputStyle} />

                        {/* Version + Explicit side by side */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                                <label style={labelStyle}>Versão</label>
                                <select value={version} onChange={e => setVersion(e.target.value)} style={inputStyle}>
                                    <option value="original">Original</option>
                                    <option value="remix">Remix</option>
                                    <option value="cover">Cover</option>
                                    <option value="live">Live</option>
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Conteúdo Explícito</label>
                                <select value={explicit} onChange={e => setExplicit(e.target.value)} style={inputStyle}>
                                    <option value="0">Não</option>
                                    <option value="1">Sim</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add to queue button */}
                <button type="submit"
                    style={{ marginTop: 20, width: '100%', padding: '12px 0', borderRadius: 10, border: '2px solid #6366f1', background: 'transparent', color: '#a5b4fc', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                    + Adicionar à fila de upload
                </button>
            </form>

            {/* Queue */}
            <div style={{ padding: '0 40px 40px' }}>
                <h3 style={{ margin: '0 0 12px', color: '#f9fafb', fontSize: 16, fontWeight: 600 }}>
                    Fila de upload {dataList.length > 0 && <span style={{ color: '#9ca3af', fontWeight: 400 }}>({dataList.length})</span>}
                </h3>

                {dataList.length === 0 ? (
                    <div style={{ background: '#1f2937', borderRadius: 12, padding: 32, textAlign: 'center' }}>
                        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Nenhuma música na fila. Preenche o formulário acima e clica "Adicionar à fila".</p>
                    </div>
                ) : (
                    <>
                        <div style={{ background: '#1f2937', borderRadius: 12, overflow: 'hidden' }}>
                            {dataList.map((item, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: i < dataList.length - 1 ? '1px solid #374151' : 'none' }}>
                                    <span style={{ color: '#6b7280', fontSize: 13, width: 24, textAlign: 'center' }}>{i + 1}</span>
                                    <img src={item.image.result} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ margin: 0, color: '#f9fafb', fontSize: 14, fontWeight: 500 }}>{item.title}</p>
                                        <p style={{ margin: '2px 0 0', color: '#9ca3af', fontSize: 12 }}>
                                            {item.genre} · {item.country}
                                            {item.release_title && <span> · {item.release_title}</span>}
                                            {item.version !== 'original' && <span> · {item.version}</span>}
                                        </p>
                                    </div>
                                    <button onClick={() => removeFromList(i)}
                                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                        Remover
                                    </button>
                                </div>
                            ))}
                        </div>

                        <button onClick={uploadAll} disabled={uploading}
                            style={{ marginTop: 16, width: '100%', padding: '14px 0', borderRadius: 10, border: 'none', background: '#6366f1', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: uploading ? 0.6 : 1 }}>
                            Fazer upload de {dataList.length} música{dataList.length > 1 ? 's' : ''}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
